import styled, { css } from 'styled-components';
import type { Brand } from '@/domainLogic/index.ts';
import { PillContainer } from '@/app/components/search/components/Filters/HorizontalFilters/Pill/styled.ts';
import { FiltersCountContainer } from '@/app/components/search/components/Filters/HorizontalFilters/Pill/FiltersCount/styled.ts';
import { HEADER_HEIGHT, SEARCH_WIDGET_HEIGHT } from '@/app/common/constants';
import customCss from '@/app/components/hotel-detail/css/customCss.ts';
import { showOnlyOnDesktopInSearchPage } from '@/app/common/css/mixins.ts';

export const HorizontalFiltersContainer = styled.div<{
  brand: Brand;
  isIndex?: boolean;
  isDesktop: boolean;
  isComparisonToastShown?: boolean;
  isDark?: boolean;
  isWaiting?: boolean;
  isToast: boolean;
  externalHeaderEnabled?: boolean;
}>`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  z-index: ${({ isIndex }) => (isIndex ? 2 : 3)};
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundPage};
  opacity: 1;
  transition:
    opacity 0.3s ease-in,
    top 0.35s ease-out;

  position: fixed;
  top: ${({ brand }) => HEADER_HEIGHT(brand, true)}px;
  left: 0;
  box-shadow: ${({ theme }): string => theme?.kallisto?.shadow?.mediumDownShadow};
  height: 56px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  ${showOnlyOnDesktopInSearchPage};

  ${({ isToast }) =>
    !isToast &&
    css`
      @media (max-width: 1023px) {
        display: none;
      }
    `};

  ${({ externalHeaderEnabled, brand, isToast }) =>
    externalHeaderEnabled &&
    css`
      @media ${customCss.breakpoints.mdPlus} {
        background-color: ${
          isToast
            ? ({ theme }) => theme.kallisto.color.text.static.textPrimary
            : ({ theme }) => theme.kallisto.color.background.static.backgroundLayer01
        };
        ${!isToast && `top: ${HEADER_HEIGHT(brand, false) + SEARCH_WIDGET_HEIGHT}px;`}
      }
    `};

  ${({ isDesktop, isToast }) =>
    isDesktop &&
    !isToast &&
    css`
      height: 72px;

      #Pill-AllFiltersContainer {
        margin-left: 2px;
      }
      #Pill-FilterContainer {
        margin-right: 8px;
      }

      #Pill-SortContainer {
        margin-left: 9px;
        margin-right: 16px;
      }
    `};

  ${({ isToast }) =>
    isToast &&
    css`
      display: flex;
      position: fixed;
      padding: 0;
      border-radius: 24px;
      height: 44px;
      justify-content: center;
      top: unset;
      bottom: 16px;
      left: 50%;
      transform: translateX(-50%);
      width: auto;
      z-index: 3;

      picture {
        source,
        img {
          width: 16px;
          height: 16px;
        }
      }

      ${PillContainer} {
        padding: 0 12px;
        height: 44px;
        font-size: 14px;
        font-weight: 700;
        line-height: 20px;
        width: auto;

        &:first-child {
          padding-left: 16px;

          ${FiltersCountContainer} {
            left: 30px;
          }
        }

        &:last-child {
          padding-right: 16px;
        }
      }
    `}

  ${({ isToast, isComparisonToastShown }) =>
    isToast &&
    isComparisonToastShown &&
    css`
      bottom: 88px;
    `}

    ${({ isDark }) =>
      isDark &&
      css`
        background-color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
      `};

  ${({ isDark, isWaiting }) =>
    isDark &&
    isWaiting &&
    css`
      ${PillContainer} {
        opacity: 0.2;
      }
    `};
`;

export const PillsOuterContainer = styled.div<{ isIndex?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  overflow-x: auto;
  scrollbar-width: none;
  height: 100%;
  scroll-snap-type: x mandatory;
  padding: 0;

  @media (max-width: ${({ isIndex }) => (isIndex ? 767 : 1023)}px) {
    padding-left: 24px;
  }

  @media (max-width: 767px) {
    padding-left: 16px;
  }

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const PillsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  overflow-x: auto;
  scrollbar-width: none;
  height: 100%;
  scroll-snap-type: x mandatory;
  padding: 0;
  max-width: 100%;

  > button:first-child {
    margin-left: 2px;
  }

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const SustainabilityContainer = styled.span`
  display: flex;
  align-items: center;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const Spacer = styled.div`
  @media (min-width: 768px) {
    flex-grow: 1;
    min-height: 20px;
  }
`;
